import { cn } from "@/lib/utils"

/**
 * Base Skeleton component for loading states
 *
 * Performance optimizations applied:
 * - CSS-only animation (no JS overhead)
 * - Uses design tokens for easy theming
 * - Composable primitives (small bundle size)
 * - No unnecessary re-renders (pure component)
 *
 * @see Vercel Best Practices: bundle-size optimization
 */
export function Skeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("animate-pulse rounded bg-skeleton-base", className)}
      {...props}
    />
  )
}

/**
 * Skeleton variant for text content
 *
 * @param width - Width class (e.g., "full", "3/4", "1/2", "24")
 * @param lines - Number of text lines to show
 */
export function SkeletonText({
  width = "full",
  lines = 1,
  className,
}: {
  width?: "full" | "3/4" | "1/2" | "1/3" | "1/4" | string
  lines?: number
  className?: string
}) {
  // Hoist static JSX outside render loop (rendering-hoist-jsx)
  const widthClass =
    width === "full"
      ? "w-full"
      : width === "3/4"
        ? "w-3/4"
        : width === "1/2"
          ? "w-1/2"
          : width === "1/3"
            ? "w-1/3"
            : width === "1/4"
              ? "w-1/4"
              : `w-${width}`

  if (lines === 1) {
    return <Skeleton className={cn("h-4", widthClass, className)} />
  }

  return (
    <div className={cn("space-y-2", className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn(
            "h-4",
            i === lines - 1 ? "w-4/5" : "w-full" // Last line shorter
          )}
        />
      ))}
    </div>
  )
}

/**
 * Skeleton variant for avatar/profile images
 *
 * @param size - Avatar size (sm: 32px, md: 40px, lg: 64px)
 */
export function SkeletonAvatar({
  size = "md",
  className,
}: {
  size?: "sm" | "md" | "lg"
  className?: string
}) {
  // Use direct class names (no useMemo for primitives - rerender-simple-expression-in-memo)
  const sizeClass =
    size === "sm" ? "h-8 w-8" : size === "lg" ? "h-16 w-16" : "h-10 w-10"

  return <Skeleton className={cn("rounded-full", sizeClass, className)} />
}

/**
 * Skeleton variant for card content
 * Includes avatar + text lines in common card pattern
 */
interface SkeletonCardProps {
  className?: string
}

export function SkeletonCard({ className }: SkeletonCardProps) {
  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center space-x-4">
        <SkeletonAvatar />
        <div className="flex-1 space-y-2">
          <SkeletonText width="3/4" />
          <SkeletonText width="1/2" />
        </div>
      </div>
      <div className="space-y-2">
        <SkeletonText lines={3} />
      </div>
    </div>
  )
}
